import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import './viewer.css';
import { API_BASE, authHeaders } from '../config/api';
import { Button } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  GlobalOutlined,
  CodeOutlined,
  BarChartOutlined,
  ProfileOutlined,
  QuestionCircleOutlined,
  UserOutlined,
  LogoutOutlined,
} from '@ant-design/icons';

const VSidebar = () => {
  const navigate = useNavigate();

  const handleLogout = () => {
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      sessionStorage.clear();
    } catch {}
    navigate('/', { replace: true });
  };

  const [collapsed, setCollapsed] = useState(false);
  
  // Get user info from localStorage and fetch avatar from API if needed
  const [user, setUser] = useState(null);
  useEffect(() => {
    const loadUser = async () => {
      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          const parsed = JSON.parse(stored);
          setUser(parsed);
          
          // If no avatar_url in localStorage, fetch from API
          if (!parsed.avatar_url) {
            try {
              const res = await fetch(`${API_BASE}/me`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
              if (res.ok) {
                const data = await res.json();
                if (data.avatar_url) {
                  const updated = { ...parsed, avatar_url: data.avatar_url };
                  localStorage.setItem('user', JSON.stringify(updated));
                  setUser(updated);
                }
              }
            } catch {}
          }
        }
      } catch {}
    };
    loadUser();
    
    const handleStorage = (e) => { if (e.key === 'user') loadUser(); };
    window.addEventListener('storage', handleStorage);
    const handleUserUpdate = () => loadUser();
    window.addEventListener('userUpdated', handleUserUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  const width = collapsed ? 72 : 240;
  const label = (text) => (collapsed ? null : <span style={{ marginLeft: 10, fontSize: 13 }}>{text}</span>);

  return (
    <aside className="v-sidebar" style={{ width, transition: 'width .2s ease' }}>
      {/* Logo Section */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '8px 0 8px',
        marginBottom: 4,
        borderBottom: '1px solid rgba(148,163,184,0.15)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/dashboard/viewer')}>
          <img 
            src="/headerlogo.png" 
            alt="Asuma Logo" 
            style={{ 
              height: collapsed ? '24px' : '28px', 
              width: 'auto',
              transition: 'height 0.2s ease'
            }} 
          />
          {!collapsed && (
            <div style={{ marginLeft: 0 }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '700', 
                color: '#fff',
                lineHeight: 1.2
              }}>Asuma</div>
              <div style={{ 
                fontSize: '9px', 
                fontWeight: '400', 
                color: '#94a3b8',
                fontStyle: 'italic',
                marginTop: -2
              }}>Viewer Dashboard</div>
            </div>
          )}
        </div>
        <Button
          type="text"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setCollapsed((c) => !c)}
          size="small"
          style={{ color: '#fff', padding: '4px' }}
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        />
      </div>
      <nav>
        <ul>
          <li>
            <NavLink to="/dashboard/viewer" end className={({isActive}) => isActive ? 'active' : undefined} title="Dashboard">
              <DashboardOutlined />{label('Dashboard')}
            </NavLink>
          </li>

          <li className="v-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/viewer/websites" className={({isActive}) => isActive ? 'active' : undefined} title="Websites">
              <GlobalOutlined />{label('Websites')}
            </NavLink>
          </li>

          <li className="v-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/viewer/snippets" className={({isActive}) => isActive ? 'active' : undefined} title="Snippets">
              <CodeOutlined />{label('Snippets')}
            </NavLink>
          </li>

          <li className="v-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/viewer/analytics" className={({isActive}) => isActive ? 'active' : undefined} title="Analytics & Reports">
              <BarChartOutlined />{label('Analytics & Reports')}
            </NavLink>
          </li>

          <li className="v-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/viewer/profile" className={({isActive}) => isActive ? 'active' : undefined} title="Profile">
              <ProfileOutlined />{label('Profile')}
            </NavLink>
          </li>

          <li className="v-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/viewer/support" className={({isActive}) => isActive ? 'active' : undefined} title="Support">
              <QuestionCircleOutlined />{label('Support')}
            </NavLink>
          </li>

          <li className="v-divider" aria-hidden="true" />
          {/* Logout */}
          <li>
            <button onClick={handleLogout} title="Logout">
              <LogoutOutlined />{label('Logout')}
            </button>
          </li>
        </ul>
      </nav>

      {/* User Profile Section */}
      {!collapsed && user && (
        <div style={{ padding: '12px 8px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ 
              width: 36, 
              height: 36, 
              borderRadius: '50%', 
              background: '#1890ff', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0
            }}>
              {user.avatar_url ? (
                <img src={`${API_BASE}${user.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <UserOutlined style={{ color: '#fff', fontSize: 18 }} />
              )}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.name || user.email?.split('@')[0] || 'User'}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.email || ''}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version & Copyright */}
      {!collapsed && (
        <div style={{ padding: '8px 8px 10px', borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 4 }}>
          <div style={{ fontSize: 10, color: 'rgba(148,163,184,0.7)', textAlign: 'center', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, letterSpacing: '0.04em' }}>v2.4.15</div>
            <div>© {new Date().getFullYear()} Asuma Analytics</div>
            <div style={{ fontSize: 9, opacity: 0.7 }}>All rights reserved</div>
          </div>
        </div>
      )}
      {collapsed && (
        <div style={{ padding: '8px 0', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 4 }}>
          <div style={{ fontSize: 9, color: 'rgba(148,163,184,0.6)' }}>v2.4.15</div>
        </div>
      )}

    </aside>
  );
};

export default VSidebar;
